// frontend/src/components/MarketCoachChat.js
import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import "./MarketCoachChat.css";

export default function MarketCoachChat() {
  const isLocal = window.location.hostname === "localhost";
  const baseUrl = isLocal
    ? "http://localhost:5000"
    : "https://www.trendscout.club";

  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi, I'm your Market Coach. Ask me how to navigate today's regime, which categories to focus on, or how aggressive you should be.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  // 🔹 new: history state
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState(null);

  const scrollRef = useRef(null);

  // keep scroll pinned to bottom on new messages or loading
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // 🔹 load last 50 sessions once on mount
  useEffect(() => {
    async function loadHistory() {
      try {
        setLoadingHistory(true);
        const resp = await fetch(
          `${baseUrl}/api/ai/coach/history?limit=50`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (data.ok) {
          setHistory(data.items || []);
        }
      } catch (e) {
        console.error("MarketCoachChat history error:", e);
      } finally {
        setLoadingHistory(false);
      }
    }

    loadHistory();
  }, [baseUrl]);

  async function handleDeleteConversation(id) {
  const confirmDelete = window.confirm(
    "Delete this chat permanently? This cannot be undone."
  );
  if (!confirmDelete) return;

  try {
    const resp = await fetch(
      `${baseUrl}/api/ai/coach/conversation/${id}`,
      {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      }
    );
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

    // remove from local history list
    setHistory((prev) => prev.filter((h) => h.id !== id));

    // if we just deleted the active one, reset chat
    setActiveThreadId((cur) => (cur === id ? null : cur));
    if (activeThreadId === id) {
      setMessages([
        {
          role: "assistant",
          content:
            "Hi, I'm your Market Coach. Ask me how to navigate today's regime, which categories to focus on, or how aggressive you should be.",
        },
      ]);
    }
  } catch (e) {
    console.error("MarketCoachChat delete error:", e);
    alert("Failed to delete chat. Please try again.");
  }
}


  // 🔹 open a past conversation into the chat
  async function openPastConversation(item) {
    try {
      setLoading(true);
      const resp = await fetch(
        `${baseUrl}/api/ai/coach/conversation/${item.id}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      if (data.ok) {
        setMessages(data.messages || []);
        setActiveThreadId(data.id);
        setHistoryOpen(false); // close drawer after selecting
      }
    } catch (e) {
      console.error("MarketCoachChat openPastConversation error:", e);
    } finally {
      setLoading(false);
    }
  }

  async function sendMessage(e) {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || loading) return;

    const newMessages = [...messages, { role: "user", content: text }];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    try {
      const resp = await fetch(`${baseUrl}/api/ai/coach/market-coach`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          question: text,
          prevMessages: newMessages.slice(-6),
          // 🔹 if we are continuing a past thread, tell backend which one
          pastMessageId: activeThreadId || null,
        }),
      });

      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }

        const data = await resp.json();
        const aiText =
          data.answer ||
          "I couldn't generate a response. Please try again in a moment.";

        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: aiText },
        ]);

        // 🔹 remember which thread this message belongs to
        if (data.loggedId) {
          setActiveThreadId((prev) => prev || data.loggedId);
        }


      // optional: after a new answer, you might want to reload history
      // so the latest session appears in the list
      // (only if you like):
      // if (!activeThreadId && data.loggedId) { ... }
    } catch (err) {
      console.error("MarketCoachChat error:", err);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "I ran into an error while talking to the coach. Please try again.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="coach-card">
      <div className="coach-header">
        <div>
          <div className="coach-title">AI Coach</div>
          <div className="coach-subtitle">
            Context-aware guidance from Market Pulse, Macro Regime & Categories.
          </div>
        </div>

        {/* 🔹 history drawer toggle button */}
        <button
          type="button"
          className="coach-history-toggle"
          onClick={() => setHistoryOpen((v) => !v)}
          disabled={loadingHistory}
        >
          {historyOpen ? "Hide Sessions ▲" : "Last 50 Sessions ▼"}
        </button>
      </div>

      {/* 🔹 collapsible history drawer, appears just under header */}
      {historyOpen && (
        <div className="coach-history-drawer">
          {loadingHistory && (
            <div className="coach-history-loading">Loading sessions…</div>
          )}

          {!loadingHistory && history.length === 0 && (
            <div className="coach-history-empty">No past sessions yet.</div>
          )}

          {!loadingHistory && history.length > 0 && (
            <div className="coach-history-list">
                {history.map((h) => (
                  <div
                    key={h.id}
                    className={
                      "coach-history-item" +
                      (activeThreadId === h.id ? " active" : "")
                    }
                  >
                    <button
                      type="button"
                      className="coach-history-main"
                      onClick={() => openPastConversation(h)}
                    >
                      <div className="coach-history-line">
                        <span className="coach-history-date">
                          {new Date(h.asOf).toISOString().slice(0, 10)}
                        </span>
                        <span className="coach-history-direction">
                          {h.direction || "-"} ({h.macroRegime || "?"})
                        </span>
                      </div>
                      <div className="coach-history-snippet">
                        {h.firstUserMsg?.slice(0, 100) || "No question"}
                      </div>
                    </button>

                    <button
                      type="button"
                      className="coach-history-delete"
                      onClick={() => handleDeleteConversation(h.id)}
                    >
                      ✕
                    </button>
                  </div>
                ))}

            </div>
          )}
        </div>
      )}

      {/* chat window stays the same */}
      <div className="coach-chat-window" ref={scrollRef}>
        {messages.map((m, idx) => (
          <div
            key={idx}
            className={
              m.role === "user" ? "coach-msg-row user" : "coach-msg-row ai"
            }
          >
            <div
              className={
                m.role === "user"
                  ? "coach-bubble coach-bubble-user"
                  : "coach-bubble coach-bubble-ai"
              }
            >
              {m.role === "assistant" ? (
                <div className="coach-markdown">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                  >
                    {m.content}
                  </ReactMarkdown>
                </div>
              ) : (
                m.content
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="coach-msg-row ai">
            <div className="coach-bubble coach-bubble-ai">
              <span className="coach-typing-dot" />
              <span className="coach-typing-dot" />
              <span className="coach-typing-dot" />
            </div>
          </div>
        )}
      </div>

      <form className="coach-input-row" onSubmit={sendMessage}>
        <input
          className="coach-input"
          type="text"
          placeholder='Ask e.g. “Should I add risk to DeFi today?”'
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={loading}
        />
        <button
          type="submit"
          className="coach-send-btn"
          disabled={loading || !input.trim()}
        >
          {loading ? "…" : "Send"}
        </button>
      </form>
    </div>
  );
}
